import React, { useEffect, useMemo, useState } from 'react';

import {
  AppButton,
  AppModal,
  AppPagination,
  AppRadio,
  AppRadioGroup,
  AppTable,
  AppTextField,
  AppTypography,
} from '@/components/atoms';
import { CollapsibleCard } from '@/components/molecules';
import { RA2NDatePicker } from '@/components/organisms';
import { getAntdTableSortOrder } from '@/utils/antdTableSortOrder';
import { normalizeYearmonth } from '@/utils/dateConverter';
import { mbConvertKana } from '@/utils/japaneseText';
import useAppToast from '@/hooks/useAppToast';
import { getApiErrorMessage } from '@/utils/apiError';
import {
  useMeisaiIssueSettings,
  useUpdateMeisaiIssueDefault,
  useUpdateMeisaiIssueSetting,
} from '@/hooks/main/useMeisaiIssueSettings';
import {
  MEISAI_ISSUE_TYPE_DAILY,
  MEISAI_ISSUE_TYPE_MONTHLY,
  type MeisaiIssueSettingPatient,
  type MeisaiIssueSortField,
  type MeisaiIssueType,
} from '@/services/main/meisaiIssueSetting';

/**
 * 令和8年法改正 No 5 — 明細書発行設定.
 *
 * Whether a patient receives the itemised statement per visit (日別) or once a month
 * (月別). Under the revision the 明細書発行加算 is billed per issue, so this decides how
 * the checkbox on the visit form is pre-filled — 日別 ticks every visit, 月別 only the
 * earliest of the month.
 *
 * Two columns per patient: the setting in force 当月, and the one booked 翌月以降の設定.
 * Only the latter is editable, because changing the current month would move a charge
 * onto visits that already exist. Shared by A2 and R2 — the rule is identical.
 *
 * When a patient has no 翌月以降 row the radio falls back to their 当月 value, not to a
 * blanket 月別 the way R2 does. The server resolves a month by taking the newest row at
 * or before it, so a patient on 日別 with no future row stays 日別 next month — showing
 * 月別 there would tell the user something the pricing does not do.
 */
const issueTypeLabel = (value: MeisaiIssueType | null | undefined): string => {
  if (value === MEISAI_ISSUE_TYPE_DAILY) return '日別';
  if (value === MEISAI_ISSUE_TYPE_MONTHLY) return '月別';
  return '―';
};

/** Rows per page — R2 paginates this table at 5 (DataTable `displayLength: 5`). */
const PER_PAGE = 5;

const ClinicInformationMeisaiIssueSection: React.FC = () => {
  const toast = useAppToast();

  const [keywordInput, setKeywordInput] = useState('');
  const [treatmentFromInput, setTreatmentFromInput] = useState('');
  const [treatmentToInput, setTreatmentToInput] = useState('');
  const [page, setPage] = useState(1);
  // Applied filters are separate from the inputs so typing does not refetch on
  // every keystroke — the list only moves when 検索 is pressed.
  const [filters, setFilters] = useState<{
    keyword: string;
    treatment_from: string;
    treatment_to: string;
  }>({ keyword: '', treatment_from: '', treatment_to: '' });

  // BD No 5 — one column at a time, 受診者番号 ascending by default, reset by 検索/クリア.
  const [sort, setSort] = useState<{ field: MeisaiIssueSortField; direction: 'asc' | 'desc' }>({
    field: 'serial_number',
    direction: 'asc',
  });

  // Only 検索/クリア spin the 検索 button. `isFetching` also goes true for a page change
  // or a sort, so feeding it straight to the button makes 検索 spin when the user merely
  // moved to page 2 — the table's own loading overlay already covers those.
  const [isSearchFetching, setIsSearchFetching] = useState(false);

  const [pendingChange, setPendingChange] = useState<{
    patient: MeisaiIssueSettingPatient;
    issueType: MeisaiIssueType;
  } | null>(null);
  // 新規患者の初期値 only applies to patients registered from now on, so it asks first.
  const [pendingDefault, setPendingDefault] = useState<MeisaiIssueType | null>(null);

  const queryParams = useMemo(
    () => ({
      keyword: filters.keyword || undefined,
      treatment_from: filters.treatment_from || undefined,
      treatment_to: filters.treatment_to || undefined,
      per_page: PER_PAGE,
      page,
      sort: sort.field,
      direction: sort.direction,
    }),
    [filters, page, sort]
  );

  const { data, isFetching } = useMeisaiIssueSettings(queryParams);
  const updateSetting = useUpdateMeisaiIssueSetting();
  const updateDefault = useUpdateMeisaiIssueDefault();

  // Drop the flag once the fetch settles. Pressing 検索 with the same criteria serves the
  // query from cache without refetching — isFetching never rises, so this clears it there
  // too rather than leaving the button spinning forever.
  useEffect(() => {
    if (!isFetching) setIsSearchFetching(false);
  }, [isFetching]);

  const resetSort = () => setSort({ field: 'serial_number', direction: 'asc' });

  // BD No 5 — 全角英数 → 半角 and 半角カナ → 全角, so what the user typed matches how
  // patients.serial_number / patient_informations.name_kana are stored. Kanji and hiragana
  // are left exactly as typed: 'aK' carries no 'C', so とうきょう does not become トウキョウ.
  const normalizeKeyword = (value: string) => mbConvertKana(value, 'aK');

  const handleKeywordBlur = () => setKeywordInput(current => normalizeKeyword(current));

  const handleSearch = () => {
    setIsSearchFetching(true);
    setPage(1);
    resetSort();
    setFilters({
      // Normalised here as well as on blur, not only there: clicking 検索 usually blurs the
      // field first and the blur handler gets there — but Safari does not focus a button on
      // click, so the blur never fires and 「１００１」 would reach the API in full width,
      // miss the numeric branch, and be searched as a name. The conversion is idempotent,
      // so running it twice on the usual path costs nothing.
      keyword: normalizeKeyword(keywordInput),
      // The pickers hand back a wareki string (令和08年07月); the API takes YYYYMM.
      treatment_from: normalizeYearmonth(treatmentFromInput) ?? '',
      treatment_to: normalizeYearmonth(treatmentToInput) ?? '',
    });
  };

  // クリア empties the inputs AND re-runs the search, so the list returns to the
  // unfiltered state rather than leaving stale rows under blank inputs.
  const handleClear = () => {
    setIsSearchFetching(true);
    setKeywordInput('');
    setTreatmentFromInput('');
    setTreatmentToInput('');
    setPage(1);
    resetSort();
    setFilters({ keyword: '', treatment_from: '', treatment_to: '' });
  };


  // Sorting is AntD's, driven through AppTable, so the header carries the same caret pair
  // every other list on the site uses (同意書一覧 et al.) instead of a hand-drawn ⇅/▲/▼.
  //
  // `sort` here stays the single source of truth and is fed back as each column's
  // `sortOrder`: AppTable would otherwise track its own, and 検索/クリア — which reset the
  // sort — would leave the caret sitting on a column the API is no longer ordering by.
  //
  // Ascending first, then descending; picking another column starts ascending again.
  // AppTable already collapses AntD's third "unsorted" click into a flip, so the handler
  // never has to deal with a null order.
  const handleSortChange = (_pagination: unknown, _filters: unknown, sorter: unknown) => {
    const next = (Array.isArray(sorter) ? sorter[0] : sorter) as {
      columnKey?: React.Key;
      field?: React.Key;
      order?: 'ascend' | 'descend' | null;
    };
    const field = (next?.columnKey ?? next?.field) as MeisaiIssueSortField | undefined;
    if (!field) return;

    setPage(1);
    setSort({ field, direction: next?.order === 'descend' ? 'desc' : 'asc' });
  };

  const handleConfirmChange = () => {
    if (!pendingChange || !data) return;

    updateSetting.mutate(
      {
        patient_id: pendingChange.patient.patient_id,
        apply_year_month: data.next_year_month,
        issue_type: pendingChange.issueType,
      },
      {
        onSuccess: () => {
          toast.success('明細書発行設定を更新しました。');
          setPendingChange(null);
        },
        onError: err => toast.error(getApiErrorMessage(err, '明細書発行設定の更新に失敗しました。')),
      }
    );
  };

  const handleConfirmDefault = () => {
    if (pendingDefault === null) return;

    updateDefault.mutate(pendingDefault, {
      onSuccess: () => {
        toast.success('明細書発行設定の初期値を更新しました。');
        setPendingDefault(null);
      },
      onError: err => toast.error(getApiErrorMessage(err, '初期値の更新に失敗しました。')),
    });
  };

  const columns = [
    {
      title: '受診者番号',
      dataIndex: 'serial_number',
      key: 'serial_number',
      width: '14%',
      sorter: true,
      sortOrder: getAntdTableSortOrder(sort.field, 'serial_number', sort.direction),
    },
    {
      title: '受診者名',
      dataIndex: 'name',
      key: 'name',
      width: '29%',
      sorter: true,
      sortOrder: getAntdTableSortOrder(sort.field, 'name', sort.direction),
      render: (_: unknown, row: MeisaiIssueSettingPatient) => row.name ?? '―',
    },
    {
      title: '受診者名カナ',
      dataIndex: 'name_kana',
      key: 'name_kana',
      width: '29%',
      sorter: true,
      sortOrder: getAntdTableSortOrder(sort.field, 'name_kana', sort.direction),
      render: (_: unknown, row: MeisaiIssueSettingPatient) => row.name_kana ?? '',
    },
    {
      // 当月 is read-only: changing it would move a charge onto visits that already exist.
      // Not sortable either — the BD only sorts the three patient columns.
      title: '当月',
      dataIndex: 'current_issue_type',
      key: 'current_issue_type',
      width: '10%',
      render: (value: MeisaiIssueType) => issueTypeLabel(value),
    },
    {
      title: '翌月以降の設定',
      dataIndex: 'next_issue_type',
      key: 'next_issue_type',
      width: '18%',
      render: (value: MeisaiIssueType | null, row: MeisaiIssueSettingPatient) => (
        <AppRadioGroup
          className="flex gap-5 [&_.ant-radio-wrapper]:me-0"
          value={value ?? row.current_issue_type}
          onChange={e =>
            setPendingChange({ patient: row, issueType: e.target.value as MeisaiIssueType })
          }
        >
          <AppRadio value={MEISAI_ISSUE_TYPE_DAILY}>日別</AppRadio>
          <AppRadio value={MEISAI_ISSUE_TYPE_MONTHLY}>月別</AppRadio>
        </AppRadioGroup>
      ),
    },
  ];

  return (
    <CollapsibleCard title="明細書発行設定" className="mb-0">
      <div className="space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-64">
            <AppTypography.XS className="mb-1">受診者検索</AppTypography.XS>
            <AppTextField
              value={keywordInput}
              onChange={e => setKeywordInput(e.target.value)}
              onBlur={handleKeywordBlur}
              placeholder="受診者番号、受診者名、カナ"
              maxLength={255}
            />
          </div>
          <div className="w-80">
            <AppTypography.XS className="mb-1">施術期間</AppTypography.XS>
            {/* The two pickers bound each other, so an inverted range cannot be entered at
                all: pick 開始年月 = 令和08年10月 and every month up to 令和08年09月 greys out
                in 終了年月 — 10月 itself stays open, a single month being a valid range.
                Whichever end is chosen first constrains the other.

                Passed as the raw wareki string the sibling holds, in the same format the
                picker emits and parses. `undefined` rather than '' while a field is empty:
                '' means "no bound at all" and would drop the component's own 1868–2057
                limits, whereas `undefined` keeps them. */}
            <div className="flex items-center gap-2">
              <RA2NDatePicker
                picker="month"
                className="w-full"
                value={treatmentFromInput}
                onChange={setTreatmentFromInput}
                endDate={treatmentToInput || undefined}
                placeholder="開始年月"
                readOnlyInput
              />
              <span className="shrink-0">〜</span>
              <RA2NDatePicker
                picker="month"
                className="w-full"
                value={treatmentToInput}
                onChange={setTreatmentToInput}
                startDate={treatmentFromInput || undefined}
                placeholder="終了年月"
                readOnlyInput
              />
            </div>
          </div>
          <AppButton
            type="primary"
            onClick={handleSearch}
            loading={isSearchFetching && isFetching}
            className="rounded"
          >
            検索
          </AppButton>
          <AppButton onClick={handleClear} className="rounded">
            クリア
          </AppButton>

          {/* Same shape as 受診者検索 / 施術期間 above: fixed width, label on top, control
              box the height of an antd input (h-8). Without that height the radios are
              ~22px tall, and the row's items-end pushes this label below the other two. */}
          <div className="w-48 ml-3">
            <AppTypography.XS className="mb-1">新規患者の初期値</AppTypography.XS>
            <div className="flex h-10 items-center">
              <AppRadioGroup
                value={data?.default_issue_type ?? MEISAI_ISSUE_TYPE_MONTHLY}
                onChange={e => setPendingDefault(e.target.value as MeisaiIssueType)}
              >
                <AppRadio value={MEISAI_ISSUE_TYPE_DAILY}>日別</AppRadio>
                <AppRadio value={MEISAI_ISSUE_TYPE_MONTHLY}>月別</AppRadio>
              </AppRadioGroup>
            </div>
          </div>
        </div>

        {/* R2 shows the hit count between the filters and the table. */}
        <AppTypography.XS>{data?.pagination.total ?? 0}件</AppTypography.XS>

        <AppTable
          rowKey="patient_id"
          columns={columns}
          dataSource={data?.patients ?? []}
          loading={isFetching}
          pagination={false}
          onChange={handleSortChange}
          locale={{ emptyText: '該当する検索結果がありません。' }}
        />

        <div className="flex justify-center">
          <AppPagination
            current={data?.pagination.current_page ?? page}
            total={data?.pagination.total ?? 0}
            // Fall back to the size we asked for: with pageSize undefined antd assumes
            // 10, so 102 rows would offer 11 pages while the API only serves 3.
            pageSize={data?.pagination.per_page ?? PER_PAGE}
            onChange={next => setPage(next ?? 1)}
          />
        </div>
      </div>

      {/* BD No 5 — wording and buttons are fixed:
          「【{受診者名}】の翌月以降の設定を「{日別/月別}」に変更しますか？」【OK】【キャンセル】 */}
      <AppModal
        isOpen={pendingChange !== null}
        onClose={() => setPendingChange(null)}
        title="明細書発行設定の変更"
        maxWidth={480}
        onOk={handleConfirmChange}
        okText="OK"
        cancelText="キャンセル"
        confirmLoading={updateSetting.isPending}
      >
        <AppTypography.P>
          【{pendingChange?.patient.name ?? ''}】の翌月以降の設定を「
          {issueTypeLabel(pendingChange?.issueType)}」に変更しますか？
        </AppTypography.P>
      </AppModal>

      {/* BD No 5 — the clinic-wide default only reaches patients registered from now on,
          so the message says so before saving. Buttons are 【はい】/【いいえ】. */}
      <AppModal
        isOpen={pendingDefault !== null}
        onClose={() => setPendingDefault(null)}
        title="新規患者の初期値の変更"
        maxWidth={480}
        onOk={handleConfirmDefault}
        okText="はい"
        cancelText="いいえ"
        confirmLoading={updateDefault.isPending}
      >
        <AppTypography.P>
          設定後に新規患者登録したものから反映します。
          <br />
          設定値は「{issueTypeLabel(pendingDefault)}」になりますが、よろしいですか?
        </AppTypography.P>
      </AppModal>
    </CollapsibleCard>
  );
};

export default ClinicInformationMeisaiIssueSection;
